import { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ApiProvider } from "../api/context";
import { useConfig } from "../config";
import { DashboardShell } from "../dashboard/DashboardLayout";
import { createGuidePointer, findClickTarget, POINTER_TIMING, type GuidePointer, type PointerTiming } from "./pointer";
import { createTourApi } from "./tourApi";
import "./pointer.css";

/**
 * Product tours: product pages with sample data, which the call worker opens in a headless browser and
 * screen-shares into demo calls (src/worker/screen.py and src/worker/tour.py).
 */
export interface TourDriver {
  /**
   * Moves to `path`, scrolling to `anchor` if given. If the link to it is on screen, the guide's pointer first
   * highlights and clicks it. Once there, marks the page as showing `key` and highlights `[data-tour=focus]`.
   */
  show: (path: string, anchor: string | null, key: string, focus?: string | null) => void;
}

declare global {
  interface Window {
    __trylookTour?: TourDriver;
  }
}

interface Request {
  path: string;
  anchor: string | null;
  key: string;
  focus: string | null;
}

/** Dashboard pages load their sample data a moment after they render. */
const FOCUS_WAIT_MS = 1500;

export function TourRoot({ pointerTiming = POINTER_TIMING }: { pointerTiming?: PointerTiming }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [request, setRequest] = useState<Request | null>(null);
  const pointer = useRef<GuidePointer | null>(null);
  const currentPath = useRef(pathname);

  useEffect(() => {
    currentPath.current = pathname;
  }, [pathname]);

  useEffect(() => {
    const guide = createGuidePointer(document, pointerTiming);
    pointer.current = guide;
    let queue = Promise.resolve();
    window.__trylookTour = {
      show: (path, anchor, key, focus = null) => {
        // One screen change at a time: a quick second request waits for the first click to finish.
        queue = queue
          .then(async () => {
            const link = findClickTarget(document, path, anchor, currentPath.current);
            if (link) {
              await guide.click(link);
            }
            setRequest({ path, anchor, key, focus });
            void navigate(path);
          })
          .catch(() => undefined);
      },
    };
    return () => {
      delete window.__trylookTour;
      guide.destroy();
      pointer.current = null;
    };
  }, [navigate, pointerTiming]);

  useEffect(() => {
    if (!request || request.path !== pathname) {
      return;
    }
    // One frame later the new page has rendered, so the anchor exists.
    let frame = window.requestAnimationFrame(() => {
      const target = request.anchor ? document.getElementById(request.anchor) : null;
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      document.documentElement.dataset.tourScreen = request.key;

      const focus = request.focus;
      if (!focus) {
        return;
      }
      const deadline = performance.now() + FOCUS_WAIT_MS;
      const highlight = () => {
        const element = document.querySelector(`[data-tour="${focus}"]`);
        if (element) {
          pointer.current?.spotlight(element);
        } else if (performance.now() < deadline) {
          frame = window.requestAnimationFrame(highlight);
        }
      };
      highlight();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [request, pathname]);

  return <Outlet />;
}

export function TourDashboard() {
  const { siteUrl } = useConfig();
  const client = useMemo(() => createTourApi(siteUrl), [siteUrl]);
  return (
    <ApiProvider client={client}>
      <DashboardShell basePath="/tour/app" onSignOut={() => undefined} />
    </ApiProvider>
  );
}
